/**
 * TalentOS-sourced "Resume Profile" tab -- replaces the old
 * Overview/Skills/Experience/Education tabs for a résumé whose
 * analysis_engine is 'talentos'. Foundry-analysed résumés keep the existing
 * tabs untouched; this component is only ever mounted for the TalentOS case
 * (gated in ResumeDetailPage.tsx by resume.analysis_engine + the
 * SHOW_TALENTOS_RESUME_UI flag).
 *
 * Every field shown here comes from GET /talentos/resume-analysis/by-host-resume
 * (talentosApi.getResumeAnalysisByHostResume) -- no Foundry-owned field
 * appears. Fields TalentOS has no data for render "N/A", never invented.
 *
 * Project Details (below Work Experience) moved here from
 * TalentosJobProfileTab (the job side), 2026-10-07, per request -- it has no
 * backing data anywhere in the schema on either side (not jobs.*, not
 * talentos_jd_analyses.*, and no "project" field in the résumé parsing
 * schema/prompt either), so every field still renders "N/A" until real
 * project data exists to show.
 */
import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AcademicCapIcon,
  TrophyIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { talentosApi } from '@/components/talentos'
import type { TalentosResumeSkill } from '@/components/talentos'
import LoadingSpinner from '@/components/common/LoadingSpinner'

// Display labels for Skill.category (snake_case in the DB). 'tool' and
// 'framework' are kept as two separate groups -- "Tools & technologies" vs
// "Frameworks & libraries" -- matching the agreed UI reference exactly.
const CATEGORY_LABELS: Record<string, string> = {
  programming_language: 'Programming languages',
  framework: 'Frameworks & libraries',
  tool: 'Tools & technologies',
  soft_skill: 'Soft skills & leadership',
  domain_knowledge: 'Domain knowledge',
  certification: 'Certifications',
}
// Fixed display order for the Grouped view -- any category not listed here
// (shouldn't happen given the DB's CHECK constraint) falls back after these.
const CATEGORY_ORDER = ['Programming languages', 'Frameworks & libraries', 'Tools & technologies', 'Soft skills & leadership']

// Display labels/colors for ResumeSkill.corroboration_status -- the exact 3
// stored values (talentos/models.py CHECK constraint), not guessed.
const CORROBORATION_LABELS: Record<string, string> = {
  grounded_in_achievement: 'Grounded in achievement',
  mentioned_in_duties_only: 'Mentioned in duties only',
  skills_list_only: 'Listed in skills only',
}
const CORROBORATION_DOT: Record<string, string> = {
  grounded_in_achievement: 'bg-green-700',
  mentioned_in_duties_only: 'bg-amber-700',
  skills_list_only: 'bg-blue-700',
}
const CORROBORATION_PILL: Record<string, string> = {
  grounded_in_achievement: 'bg-green-100 text-green-700 border-green-200',
  mentioned_in_duties_only: 'bg-amber-50 text-amber-700 border-amber-200',
  skills_list_only: 'bg-blue-50 text-blue-700 border-blue-200',
}

const SidebarField: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5">{value ? value : <span className="text-gray-300">N/A</span>}</div>
  </div>
)

// Two-up label/value layout -- Project Details pairs fields side by side
// rather than stacking every field one per row (matches
// TalentosJobProfileTab's LabeledFieldGrid, the card's original home).
const FieldGrid: React.FC<{ fields: [string, string | null | undefined][] }> = ({ fields }) => (
  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
    {fields.map(([label, value], i) => (
      <SidebarField key={i} label={label} value={value} />
    ))}
  </div>
)

const SidebarListCard: React.FC<{ title: string; icon: React.ReactNode; items: string[] }> = ({ title, icon, items }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">{title}</h4>
    {items.length === 0 ? (
      <p className="text-sm text-gray-300">N/A</p>
    ) : (
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-800">
            <span className="mt-0.5 flex-shrink-0">{icon}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
)

interface Props {
  resumeId: string
}

const TalentosResumeProfileTab: React.FC<Props> = ({ resumeId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['talentos', 'resume-analysis', 'by-host-resume', resumeId],
    queryFn: () => talentosApi.getResumeAnalysisByHostResume(resumeId),
    enabled: !!resumeId,
  })

  const [skillSearch, setSkillSearch] = useState('')
  const [corroborationFilter, setCorroborationFilter] = useState<string>('all')
  const [skillView, setSkillView] = useState<'grouped' | 'details'>('grouped')
  const [expandedExperience, setExpandedExperience] = useState<Record<number, boolean>>({})
  const [openSections, setOpenSections] = useState<{ skills: boolean; exp: boolean; project: boolean }>({
    skills: true,
    exp: true,
    project: true,
  })

  const skills = data?.skills ?? []

  const corroborationCounts = useMemo(
    () =>
      skills.reduce<Record<string, number>>((acc, s) => {
        acc[s.corroboration_status] = (acc[s.corroboration_status] || 0) + 1
        return acc
      }, {}),
    [skills]
  )

  const filteredSkills = useMemo(
    () =>
      skills.filter((s) => {
        if (corroborationFilter !== 'all' && s.corroboration_status !== corroborationFilter) return false
        if (skillSearch && !s.canonical_name.toLowerCase().includes(skillSearch.toLowerCase())) return false
        return true
      }),
    [skills, corroborationFilter, skillSearch]
  )

  const groupedSkills = useMemo(() => {
    const groups = filteredSkills.reduce<Record<string, TalentosResumeSkill[]>>((acc, s) => {
      const label = CATEGORY_LABELS[s.category] || s.category
      if (!acc[label]) acc[label] = []
      acc[label].push(s)
      return acc
    }, {})
    const ordered: Record<string, TalentosResumeSkill[]> = {}
    for (const label of CATEGORY_ORDER) {
      if (groups[label]) ordered[label] = groups[label]
    }
    for (const label of Object.keys(groups)) {
      if (!ordered[label]) ordered[label] = groups[label]
    }
    return ordered
  }, [filteredSkills])

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load TalentOS profile data.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        TalentOS has not analysed this résumé yet — it may still be processing.
      </div>
    )
  }

  const fullName = data.candidate_full_name || data.full_name

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {/* Skills */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, skills: !prev.skills }))}
            className="flex items-center gap-2 mb-4 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Skills</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {skills.length}
            </span>
            {openSections.skills ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>

          {openSections.skills && (
          <>
          <div className="relative mb-3">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={skillSearch}
              onChange={(e) => setSkillSearch(e.target.value)}
              placeholder="Search skills"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setCorroborationFilter('all')}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${
                corroborationFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> All {skills.length}
            </button>
            {Object.entries(CORROBORATION_LABELS).map(([key, label]) =>
              corroborationCounts[key] ? (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCorroborationFilter(key)}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${
                    corroborationFilter === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${CORROBORATION_DOT[key]}`} /> {label} {corroborationCounts[key]}
                </button>
              ) : null
            )}
            <div className="ml-auto inline-flex rounded-md border border-gray-200 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setSkillView('grouped')}
                className={`px-3 py-1 ${skillView === 'grouped' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
              >
                Grouped
              </button>
              <button
                type="button"
                onClick={() => setSkillView('details')}
                className={`px-3 py-1 border-l border-gray-200 ${skillView === 'details' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
              >
                All details
              </button>
            </div>
          </div>

          {filteredSkills.length === 0 ? (
            <p className="text-sm text-gray-400">No skills match this filter.</p>
          ) : skillView === 'grouped' ? (
            <div className="space-y-4">
              {Object.entries(groupedSkills).map(([label, items]) => (
                <div key={label}>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {label} <span className="text-gray-400">({items.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((s, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full bg-gray-50 border border-gray-200"
                        title={s.evidence_snippet || undefined}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${CORROBORATION_DOT[s.corroboration_status] || 'bg-gray-400'}`} />
                        {s.canonical_name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4">Skill</th>
                    <th className="py-2 pr-4">Category</th>
                    <th className="py-2 pr-4">Evidence</th>
                    <th className="py-2 pr-4">Proficiency</th>
                    <th className="py-2 pr-4">Last used</th>
                    <th className="py-2">Specificity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkills.map((s, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-900 whitespace-nowrap">{s.canonical_name}</td>
                      <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{CATEGORY_LABELS[s.category] || s.category}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${
                            CORROBORATION_PILL[s.corroboration_status] || 'bg-gray-50 text-gray-600 border-gray-200'
                          }`}
                        >
                          {CORROBORATION_LABELS[s.corroboration_status] || s.corroboration_status}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">
                        {s.proficiency_label || '—'}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{s.last_used_year != null ? s.last_used_year : '—'}</td>
                      <td className="py-2 text-gray-600 whitespace-nowrap">
                        {s.specificity_score != null ? s.specificity_score.toFixed(2) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </div>

        {/* Work Experience */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, exp: !prev.exp }))}
            className="flex items-center gap-2 mb-4 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Work Experience</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {data.career_history.length}
            </span>
            {openSections.exp ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.exp && (
          <div className="space-y-5">
            {data.career_history.length === 0 && <p className="text-sm text-gray-400">No work experience extracted.</p>}
            {data.career_history
              .slice()
              .sort((a, b) => a.sequence_order - b.sequence_order)
              .map((entry, idx) => {
                const isOpen = expandedExperience[idx] ?? idx === 0
                const quantifiedCount = entry.achievements.filter((a) => a.is_quantified).length
                return (
                  <div key={idx} className="relative pl-5 border-l-2 border-gray-100 last:border-l-0 last:pl-5">
                    <span
                      className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${
                        idx === 0 && !entry.end_date ? 'bg-blue-600' : 'border-2 border-gray-300 bg-white'
                      }`}
                    />
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium text-gray-900">{entry.title || 'Untitled role'}</div>
                        <div className="text-sm text-blue-600">{entry.company_name}</div>
                        {entry.normalized_title && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Normalized title: {entry.normalized_title}
                            {entry.seniority_rank != null ? ` (rank ${entry.seniority_rank})` : ''}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500 whitespace-nowrap">
                        {entry.start_date} – {entry.end_date || 'Present'}
                        {entry.duration_months != null && (
                          <span className="ml-2 inline-flex text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                            {entry.duration_months} mo
                          </span>
                        )}
                      </div>
                    </div>
                    {entry.description_summary && <p className="text-sm text-gray-700 mt-2">{entry.description_summary}</p>}
                    {entry.achievements.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setExpandedExperience((prev) => ({ ...prev, [idx]: !isOpen }))}
                        className="text-sm text-blue-600 hover:text-blue-700 mt-2 inline-flex items-center gap-1"
                      >
                        {isOpen ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
                        {isOpen
                          ? 'Hide details'
                          : `${entry.achievements.length} achievement${entry.achievements.length === 1 ? '' : 's'}${
                              quantifiedCount ? ` · ${quantifiedCount} quantified` : ''
                            }`}
                      </button>
                    )}
                    {isOpen && entry.achievements.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {entry.achievements.map((a, ai) => (
                          <div key={ai} className="bg-gray-50 rounded-md p-3">
                            <p className="text-sm text-gray-700">{a.statement_text}</p>
                            {a.is_quantified && a.quantified_metric && (
                              <span className="inline-flex mt-2 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                                {a.quantified_metric}
                              </span>
                            )}
                            <p className="text-xs text-gray-400 mt-1.5">
                              Plausible context: {a.has_plausible_context ? 'Yes' : 'No'}
                              {a.specificity_score != null ? ` · Specificity ${a.specificity_score.toFixed(2)}` : ''}
                              {a.impact_score != null ? ` · Impact ${a.impact_score.toFixed(2)}` : ''}
                              {a.linked_skill_ids.length > 0 ? ` · Linked skills: ${a.linked_skill_ids.length}` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
          )}
        </div>

        {/* Project Details -- not present anywhere in our schema: no
            "project" field in the résumé parsing schema/prompt. Every field
            here is a genuine gap, not an assumption -- report to
            TalentOS/AIRecruit team if this is wanted. */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <button
            type="button"
            onClick={() => setOpenSections((prev) => ({ ...prev, project: !prev.project }))}
            className="flex items-center gap-2 mb-4 w-full text-left"
            title="Collapse / expand"
          >
            <h3 className="text-base font-semibold text-gray-900">Project Details</h3>
            {openSections.project ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
            )}
          </button>
          {openSections.project && (
            <FieldGrid
              fields={[
                ['Project name', null],
                ['Project type', null],
                ['Duration', null],
                ['Team size', null],
                ['Tech stack', null],
                ['Project description', null],
              ]}
            />
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Candidate Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Candidate Information</h4>
          <div className="space-y-3">
            <SidebarField label="Full name" value={fullName} />
            <SidebarField label="Email" value={data.candidate_email} />
            <SidebarField label="Phone" value={data.candidate_phone} />
            <SidebarField label="Location" value={data.location} />
            <SidebarField label="Current company" value={data.current_company} />
            <SidebarField label="Current title" value={data.current_title} />
            <SidebarField
              label="Total experience"
              value={data.total_experience_years != null ? `${data.total_experience_years} yrs` : null}
            />
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Domain</div>
            {data.domain.length === 0 ? (
              <p className="text-sm text-gray-300">N/A</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.domain.map((d, i) => (
                  <span key={i} className="text-xs bg-indigo-50 text-indigo-700 rounded-full px-2 py-1">
                    {d}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <SidebarListCard title="Education" icon={<AcademicCapIcon className="w-4 h-4 text-gray-400" />} items={data.education_summary} />
        <SidebarListCard title="Certifications" icon={<TrophyIcon className="w-4 h-4 text-amber-500" />} items={data.certifications} />
        <SidebarListCard title="Publications" icon={<DocumentTextIcon className="w-4 h-4 text-gray-400" />} items={data.publications} />
        <SidebarListCard title="Other Qualities" icon={<span className="text-gray-300 mt-0.5">•</span>} items={data.other_qualifications} />
      </div>
    </div>
  )
}

export default TalentosResumeProfileTab
